// Corp Tower — final end-game map. A vertical corporate tower climbed
// floor by floor, culminating in the legendary CEO suite at the top.
// Shares the RouteMap interface defined in route1.ts.

import { TileType, type RoomData } from "@/engine/constants";
import type {
  EncounterZone,
  MapExit,
  RouteMap,
  TrainerPlacement,
} from "./route1";

// ─── Layout constants ─────────────────────────────────────────────────────────

const WIDTH = 16;
const HEIGHT = 32;

// Elevator shaft: center column x 7-8 (2 tiles wide), full height.
const ELEVATOR_X_START = 7;
const ELEVATOR_X_END = 9; // exclusive

// ─── Tile builder ─────────────────────────────────────────────────────────────

function buildTileMaps(): { tileMap: number[][]; collisionMap: boolean[][] } {
  const tileMap: number[][] = [];
  const collisionMap: boolean[][] = [];

  for (let y = 0; y < HEIGHT; y++) {
    const tileRow: number[] = [];
    const collRow: boolean[] = [];

    for (let x = 0; x < WIDTH; x++) {
      const onBorder = x === 0 || x === WIDTH - 1 || y === 0 || y === HEIGHT - 1;
      const onElevator = x >= ELEVATOR_X_START && x < ELEVATOR_X_END;

      // CEO suite: y 0-3
      const onCeoSuite = y >= 0 && y <= 3;
      // Executive floor 3: y 4-10
      const onFloor3 = y >= 4 && y <= 10;
      // Transition corridor 1: y 11
      const onCorridor1 = y === 11;
      // Executive floor 2: y 12-18
      const onFloor2 = y >= 12 && y <= 18;
      // Transition corridor 2: y 19
      const onCorridor2 = y === 19;
      // Executive floor 1: y 20-26
      const onFloor1 = y >= 20 && y <= 26;
      // Lobby / base: y 27-31
      const onLobby = y >= 27 && y <= 31;

      let tile: TileType;
      let blocked: boolean;

      if (onBorder) {
        tile = TileType.Wall;
        blocked = true;
      } else if (onElevator) {
        tile = TileType.Boardwalk;
        blocked = false;
      } else if (onCeoSuite) {
        // CEO floor: office tile (Wall acts as office interior wall)
        // Interior is walkable Floor tiles; outer ring already handled by border
        tile = TileType.BuildingFloor;
        blocked = false;
      } else if (onFloor3 || onFloor2 || onFloor1) {
        // Arena sections: Grass for encounter zones, flanked by Wall offices
        const onWallColumn = x <= 1 || x >= WIDTH - 2;
        if (onWallColumn) {
          tile = TileType.Wall;
          blocked = true;
        } else {
          tile = TileType.Grass;
          blocked = false;
        }
      } else if (onCorridor1 || onCorridor2) {
        // Narrow corridor between floors — floor tile, walkable
        tile = TileType.BuildingFloor;
        blocked = false;
      } else if (onLobby) {
        // Ground floor lobby
        tile = TileType.BuildingFloor;
        blocked = false;
      } else {
        tile = TileType.BuildingFloor;
        blocked = false;
      }

      tileRow.push(tile);
      collRow.push(blocked);
    }

    tileMap.push(tileRow);
    collisionMap.push(collRow);
  }

  return { tileMap, collisionMap };
}

const { tileMap, collisionMap } = buildTileMaps();

// ─── Encounter zones ──────────────────────────────────────────────────────────

const encounterZones: EncounterZone[] = [
  {
    id: "floor_1",
    bounds: { x: 1, y: 22, w: 14, h: 6 },
    speciesPool: [
      { speciesId: "10", weight: 25, levelMin: 25, levelMax: 35 }, // Sysmonk
      { speciesId: "6",  weight: 25, levelMin: 25, levelMax: 35 }, // Stonkzilla
      { speciesId: "24", weight: 25, levelMin: 28, levelMax: 38 }, // Inframonk
      { speciesId: "22", weight: 25, levelMin: 28, levelMax: 38 }, // Algomancer
    ],
  },
  {
    id: "floor_2",
    bounds: { x: 1, y: 13, w: 14, h: 6 },
    speciesPool: [
      { speciesId: "28", weight: 25, levelMin: 35, levelMax: 45 }, // Scalelord
      { speciesId: "25", weight: 25, levelMin: 35, levelMax: 45 }, // Pitchmaster
      { speciesId: "18", weight: 25, levelMin: 32, levelMax: 42 }, // Trendlion
      { speciesId: "20", weight: 25, levelMin: 32, levelMax: 42 }, // Auditlord
    ],
  },
  {
    id: "floor_3",
    bounds: { x: 1, y: 5, w: 14, h: 6 },
    speciesPool: [
      { speciesId: "29", weight: 30, levelMin: 45, levelMax: 55 }, // Synthlord
      { speciesId: "30", weight: 30, levelMin: 45, levelMax: 55 }, // Visionfox
      { speciesId: "11", weight: 20, levelMin: 48, levelMax: 58 }, // Cratus
      { speciesId: "12", weight: 20, levelMin: 48, levelMax: 58 }, // Huskii
    ],
  },
];

// ─── Exits ────────────────────────────────────────────────────────────────────

const exits: MapExit[] = [
  { x: 8, y: 31, targetMapId: "city_district", targetX: 11, targetY: 0 },
  { x: 8, y: 31, targetMapId: "volcano_peak",  targetX: 10, targetY: 0 },
];

// ─── Trainers ─────────────────────────────────────────────────────────────────

const trainers: TrainerPlacement[] = [
  { trainerId: "trainer_tower_floor1", x: 4,  y: 25 },
  { trainerId: "trainer_tower_floor2", x: 12, y: 16 },
  { trainerId: "trainer_tower_floor3", x: 4,  y: 7  },
  { trainerId: "trainer_tower_ceo",    x: 8,  y: 1  },
];

// ─── Export ───────────────────────────────────────────────────────────────────

// RouteMap extends RoomData; RoomData does not yet include the extended fields
// (encounterZones, exits, trainers) — they live only in this file's interface.
// Cast via unknown to satisfy the structural check until constants.ts is updated.
export const CORP_TOWER_MAP: RouteMap = {
  id: "corp_tower",
  name: "Corp Tower",
  type: "overworld",
  width: WIDTH,
  height: HEIGHT,
  tileMap,
  collisionMap,
  interactables: [],
  encounterZones,
  exits,
  trainers,
} satisfies RouteMap;
